import { opportunityPartyName } from "@/lib/opportunity-calc";
import type { DragEvent } from "react";
import { Avatar } from "@/components/shared/Avatar";
import type { OpportunityWithRelations } from "@/hooks/use-opportunities";
import type { OfferWithCustomer } from "@/hooks/use-offers-queries";
import { formatDateShort, formatNOK } from "@/lib/format";
import { OFFER_PROBABILITY_LABELS } from "@/lib/offer-pipeline-calc";
import { cn } from "@/lib/utils";
import { TONE_TEXT } from "@/lib/widget-status";

interface PipeCardProps {
  opportunity: OpportunityWithRelations;
  offer: OfferWithCustomer | null;
  days: number;
  stalled: boolean;
  dragging: boolean;
  dragProps: { draggable: boolean; onDragStart: (e: DragEvent) => void; onDragEnd: () => void };
  onOpen: () => void;
}

/** Kort i pipen: kunde, tittel, eier, dager i fasen og, i Tilbud, beløp og sannsynlighet. */
export default function PipeCard({ opportunity, offer, days, stalled, dragging, dragProps, onOpen }: PipeCardProps) {
  return (
    <div
      role="button"
      tabIndex={0}
      {...dragProps}
      onClick={onOpen}
      onKeyDown={(e) => {
        if (e.target === e.currentTarget && (e.key === "Enter" || e.key === " ")) {
          e.preventDefault();
          onOpen();
        }
      }}
      aria-label={[
        `Åpne prosess: ${opportunity.title}`,
        opportunityPartyName(opportunity),
        days === 1 ? "1 dag i fasen" : `${days} dager i fasen`,
        stalled ? "står stille" : null,
        offer ? formatNOK(offer.total_amount) : null,
      ]
        .filter(Boolean)
        .join(", ")}
      className={cn(
        "w-full space-y-2 border-[1.5px] border-foreground bg-card p-3 text-left hover:bg-muted/40",
        dragProps.draggable && "cursor-grab",
        dragging && "opacity-50",
      )}
    >
      <div className="flex items-start justify-between gap-2">
        <span className="min-w-0">
          <span className="block truncate text-sm font-semibold">{opportunityPartyName(opportunity)}</span>
          <span className="block truncate text-sm">{opportunity.title}</span>
          {opportunity.theme && <span className="block truncate text-xs text-muted-foreground">{opportunity.theme}</span>}
        </span>
        <Avatar name={opportunity.owner?.full_name} avatarUrl={opportunity.owner?.avatar_url} size="sm" />
      </div>

      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs">
        <span className={cn(stalled ? TONE_TEXT.miss : "text-muted-foreground")}>
          {days === 1 ? "1 dag" : `${days} dager`}
          {stalled ? " (står stille)" : ""}
        </span>
        {offer && (
          <>
            <span className="font-medium">{formatNOK(offer.total_amount)}</span>
            <span className="text-muted-foreground">
              {offer.probability ? OFFER_PROBABILITY_LABELS[offer.probability] : "Sett sannsynlighet"}
            </span>
            {offer.expected_close_date && (
              <span className="text-muted-foreground">Close {formatDateShort(offer.expected_close_date)}</span>
            )}
          </>
        )}
      </div>
    </div>
  );
}
